import { Utills } from "../../../../../shared/app-configs/utilit";

import { roundTcV4Decimal } from "../../tc-v4-decimals";

export interface ProductFormData {
  productInfo: ITCProduct;
  rawMaterialInfo: ITcProductRawMaterialInformation[];
  geographicInfo: ITcProductRawMaterialGeographicInformation[];
  productCertifiedWeightInfo: ITcProductCertifiedWeightInformation[];
  productFacilitiesInfo: ITcProductFacilitiesInformation[];
  productFirstProcessorInfo: ITcProductFirstProcessorInformation[];
  additionalInfo: IAdditionalInfo;
}
export interface Option {
  value: string | number;
  label: string;
}

export const normalizeStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item).trim())
      .filter((item) => item.length > 0);
  }

  if (typeof value === "string" && value.trim()) {
    return [value.trim()];
  }

  return [];
};

export interface ITCProduct {
  tcProductOrderNo: string[];
  tcProductArticleNo: string;
  tcProductProductionDate: string;
  tcProductShipmentDocNo: string[];
  tcProductNumberOfUnits: string;
  tcProductUnitsMetric: string;
  tcProductUnitsMetricOther: string;
  // Weight fields hold "" until the user types a value (no default 0 —
  // 86ajey1xz); the payload mappers coerce empty back to 0 on save.
  tcProductNetShippingWeight: number | string;
  tcProductSupplementaryWeight: number | string;
  tcProductInfoSupplementaryWeight: string;
  tcProductAdditives: string;
  tcProductAdditivesWeight: number | string;
  tcProductCategoryCode: string;
  tcProductCategoryUserSpecificTerm: string;
  tcProductDetailCode: string;
  tcProductDetailUserSpecificTerm: string;
  tcProductCategoryCodeDescription: string;
  tcProductDetailCodeDescription: string;
}

export interface IAdditionalInfo {
  tcProductInfoFiberLength: string;
  tcProductInfoYarnCount: string;
  tcProductInfoFabricWeightAndConstruct: string;
  tcProductInfoFabricWeightOfFinishedProduct: string;
  tcProductInfoAdditional: string;
  tcProductAdditives: string;
  tcProductAdditivesWeight: number | string;
  tcProductFeedstock: string[]; // Now array for chip input
  tcProductPercentTextileWaste: string;
  tcProductPackagingFor: string[];
  tcProductGtin: string;
  tcProductReclaimedMaterialSource: string[]; // Now array for chip input
  tcProductInfoTechnicalSpecifications: string;
  tcProductOriginOrganizationTeId: string[];
  tcProductOriginFacilityTeId: string[];
}

export interface ITcProductRawMaterialInformation {
  tcProductRawMaterialCode: string;
  tcProductRawMaterialPercentage: number | string;
  tcProductRawMaterialUserSpecificTerm: string;
  tcProductRawMaterialCodeDescription: string;
  // Wireframe Step 2 extras (not part of the official TC v4 model — persisted on the RM row so the
  // stepper repopulates on edit; the final-JSON whitelist strips them from the submitted JSON):
  claimsCategory?: string; // user-entered claims category (certified rows only)
  certified?: boolean; // auto-derived: RM code ∩ TC standard certified-RM mapping
}

export interface ITcProductRawMaterialGeographicInformation {
  tcProductCertifiedRawMaterialCode: string;
  tcProductCertifiedRawMaterialCodeDescription: string;
  tcProductCertifiedRawMaterialWeight: number;
  tcProductCertifiedRawMaterialCountryOrArea: string;
  tcProductCertifiedRawMaterialCountryOrAreaDescription: string;
  tcProductCertifiedRawMaterialStateOrProvince: string;
  tcProductCertifiedRawMaterialStateOrProvinceDescription: string;
}

export const flattenCertifiedRawMaterialGeographicInformation = (
  value: any
): ITcProductRawMaterialGeographicInformation[] => {
  const rows = Array.isArray(value) ? value : [];
  return rows.flatMap((item: any) => {
    const weightRows = Array.isArray(item?.tcProductCertifiedRawMaterialWeightInformation)
      ? item.tcProductCertifiedRawMaterialWeightInformation
      : null;

    if (weightRows) {
      return weightRows.map((weightItem: any) => ({
        tcProductCertifiedRawMaterialCode: item.tcProductCertifiedRawMaterialCode || "",
        tcProductCertifiedRawMaterialCodeDescription:
          item.tcProductCertifiedRawMaterialCodeDescription || "",
        tcProductCertifiedRawMaterialWeight:
          parseFloat(weightItem?.tcProductCertifiedRawMaterialWeight?.toString() || "0") || 0,
        tcProductCertifiedRawMaterialCountryOrArea:
          weightItem?.tcProductCertifiedRawMaterialCountryOrArea || "",
        tcProductCertifiedRawMaterialCountryOrAreaDescription:
          weightItem?.tcProductCertifiedRawMaterialCountryOrAreaDescription || "",
        tcProductCertifiedRawMaterialStateOrProvince:
          weightItem?.tcProductCertifiedRawMaterialStateOrProvince || "",
        tcProductCertifiedRawMaterialStateOrProvinceDescription:
          weightItem?.tcProductCertifiedRawMaterialStateOrProvinceDescription || "",
      }));
    }

    return [
      {
        tcProductCertifiedRawMaterialCode: item?.tcProductCertifiedRawMaterialCode || "",
        tcProductCertifiedRawMaterialCodeDescription:
          item?.tcProductCertifiedRawMaterialCodeDescription || "",
        tcProductCertifiedRawMaterialWeight:
          parseFloat(item?.tcProductCertifiedRawMaterialWeight?.toString() || "0") || 0,
        tcProductCertifiedRawMaterialCountryOrArea:
          item?.tcProductCertifiedRawMaterialCountryOrArea || "",
        tcProductCertifiedRawMaterialCountryOrAreaDescription:
          item?.tcProductCertifiedRawMaterialCountryOrAreaDescription || "",
        tcProductCertifiedRawMaterialStateOrProvince:
          item?.tcProductCertifiedRawMaterialStateOrProvince || "",
        tcProductCertifiedRawMaterialStateOrProvinceDescription:
          item?.tcProductCertifiedRawMaterialStateOrProvinceDescription || "",
      },
    ];
  });
};

export const groupCertifiedRawMaterialGeographicInformation = (value: any) => {
  const grouped = new Map<string, any>();
  flattenCertifiedRawMaterialGeographicInformation(value).forEach((item) => {
    const code = item.tcProductCertifiedRawMaterialCode || "";
    if (!grouped.has(code)) {
      grouped.set(code, {
        tcProductCertifiedRawMaterialCode: code,
        tcProductCertifiedRawMaterialCodeDescription:
          item.tcProductCertifiedRawMaterialCodeDescription || "",
        tcProductCertifiedRawMaterialWeightInformation: [],
      });
    }
    grouped.get(code).tcProductCertifiedRawMaterialWeightInformation.push({
      tcProductCertifiedRawMaterialWeight:
        parseFloat(item.tcProductCertifiedRawMaterialWeight?.toString() || "0") || 0,
      tcProductCertifiedRawMaterialCountryOrArea:
        item.tcProductCertifiedRawMaterialCountryOrArea || "",
      tcProductCertifiedRawMaterialCountryOrAreaDescription:
        item.tcProductCertifiedRawMaterialCountryOrAreaDescription || "",
      tcProductCertifiedRawMaterialStateOrProvince:
        item.tcProductCertifiedRawMaterialStateOrProvince || "",
      tcProductCertifiedRawMaterialStateOrProvinceDescription:
        item.tcProductCertifiedRawMaterialStateOrProvinceDescription || "",
    });
  });
  return Array.from(grouped.values());
};

export interface ITcProductCertifiedWeightInformation {
  tcProductClaimsCategory: string;
  tcProductCertifiedWeight: number;
  tcProductClaimsCategoryCertifiedWeight?: number;
  tcProductRawMaterialCodeClaimsCategory: string;
  tcProductRawMaterialCodeClaimsCategoryDescription: string;
}

export interface ITcProductFacilitiesInformation {
  tcProductFacilitiesTeId: string;
  tcProductFacilitiesProcessCode: string | string[];
}

export const toStringArray = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }
  const text = String(value || "").trim();
  return text ? [text] : [];
};

// Draft docs and the product GET can carry tcProductAdditives as an array
// (e.g. ["6"] or []), but the step-1 form schema and the canonical final
// JSON type it as a string — coerce at the mapping boundary so an array
// never reaches Yup.string() (it fails as a hidden type error and blocks
// the with-component edit stepper).
export const toAdditivesString = (value: any): string => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item ?? "").trim()).filter(Boolean).join(", ");
  }
  return String(value ?? "");
};

export const normalizeProductFacilityInformation = (
  value: any
): ITcProductFacilitiesInformation[] => {
  const rows = Array.isArray(value) ? value : [];
  return rows.map((item: any) => ({
    tcProductFacilitiesTeId:
      item?.tcProductFacilitiesTeId || item?.tcProductFacilityTeId || "",
    tcProductFacilitiesProcessCode:
      toStringArray(item?.tcProductFacilitiesProcessCode || item?.tcProductFacilityProcessCode),
  }));
};

export const mapProductFacilitiesToPayload = (value: any) =>
  normalizeProductFacilityInformation(value).map((item) => ({
    tcProductFacilityTeId: item.tcProductFacilitiesTeId || "",
    tcProductFacilityProcessCode: toStringArray(item.tcProductFacilitiesProcessCode),
  }));

export interface ITcProductFirstProcessorInformation {
  tcProductFirstProcessorTeId: string;
  tcProductFirstProcessCode: string | string[];
}

export interface ICertifiedProductDetails {
  tcId: string;
}

// ===========================================================================
// Product WITH Component (TC v4) — wireframe model
// A product = sum of components; each component = sum of raw materials.
// These mirror the backend post-component-detail-v4 contract exactly.
// ===========================================================================

export interface ITcComponentRawMaterialInformation {
  tcProductComponentRawMaterialCode: string;
  // Empty string keeps a new number input visually blank; payload mapping
  // converts a saved value back to a number.
  tcProductComponentRawMaterialPercentage: number | string;
  // UI-only extras (stripped from the submitted JSON by the backend whitelist):
  tcProductComponentRawMaterialCodeDescription?: string;
  claimsCategory?: string; // user-entered claims category (certified rows only)
  certified?: boolean; // auto-derived: RM code ∩ TC standard certified-RM mapping
}

export interface ITcComponentCertifiedWeightInformation {
  tcProductComponentClaimsCategory: string;
  tcProductComponentCertifiedWeight: number;
}

export interface ITcProductComponentInformation {
  // UI-only identity (not part of the backend body; used to track rows in the workspace):
  componentId?: string;
  tcAssociatedProductArticleNo: string;
  tcProductComponentNo: string;
  tcProductComponentDetail: string;
  // "" until the user types a value (no default 0 — 86ajey1xz); coerced on save.
  tcProductComponentNetShippingWeight: number | string;
  tcProductComponentInfoFiberLength?: string;
  tcProductComponentInfoYarnCount?: string;
  tcProductComponentInfoFabricWeightAndConstruct?: string;
  tcProductComponentInfoFabricWeightOfFinishedProduct?: string;
  tcProductComponentInfoTechnicalSpecifications?: string;
  tcProductComponentInfoAdditional?: string;
  tcProductComponentCertifiedWeightInformation: ITcComponentCertifiedWeightInformation[];
  tcProductComponentRawMaterialInformation: ITcComponentRawMaterialInformation[];
}

// A workspace component row carries the full component plus its derived weights for the table.
export interface ComponentWorkspaceRow extends ITcProductComponentInformation {
  componentNetShippingWeight: number;
  componentCertifiedWeight: number;
  componentNonCertifiedWeight: number;
}

/**
 * Build the POST body for post-component-detail-v4. The backend ignores any
 * tcProductComponentInformation sent on the product payload and rebuilds the
 * component list from these separately-posted components, so the body always
 * wraps exactly one component in a single-element array.
 */
export const mapComponentToPayload = (
  tcId: string,
  productId: string,
  component: ITcProductComponentInformation
) => {
  return {
    tcId,
    productId,
    ...(component.componentId ? { componentId: component.componentId } : {}),
    tcProductComponentInformation: [
      {
        tcAssociatedProductArticleNo: component.tcAssociatedProductArticleNo || "",
        tcProductComponentNo: component.tcProductComponentNo || "",
        tcProductComponentDetail: component.tcProductComponentDetail || "",
        tcProductComponentNetShippingWeight:
          parseFloat(component.tcProductComponentNetShippingWeight?.toString() || "0") || 0,
        tcProductComponentInfoFiberLength: component.tcProductComponentInfoFiberLength || "",
        tcProductComponentInfoYarnCount: component.tcProductComponentInfoYarnCount || "",
        tcProductComponentInfoFabricWeightAndConstruct:
          component.tcProductComponentInfoFabricWeightAndConstruct || "",
        tcProductComponentInfoFabricWeightOfFinishedProduct:
          component.tcProductComponentInfoFabricWeightOfFinishedProduct || "",
        tcProductComponentInfoTechnicalSpecifications:
          component.tcProductComponentInfoTechnicalSpecifications || "",
        tcProductComponentInfoAdditional: component.tcProductComponentInfoAdditional || "",
        tcProductComponentCertifiedWeightInformation: (
          component.tcProductComponentCertifiedWeightInformation || []
        ).map((item) => ({
          tcProductComponentClaimsCategory: item.tcProductComponentClaimsCategory || "",
          tcProductComponentCertifiedWeight:
            parseFloat(item.tcProductComponentCertifiedWeight?.toString() || "0") || 0,
        })),
        tcProductComponentRawMaterialInformation: (
          component.tcProductComponentRawMaterialInformation || []
        ).map((item) => ({
          tcProductComponentRawMaterialCode: item.tcProductComponentRawMaterialCode || "",
          tcProductComponentRawMaterialPercentage:
            roundTcV4Decimal(
              parseFloat(item.tcProductComponentRawMaterialPercentage?.toString() || "0") || 0
            ),
        })),
      },
    ],
  };
};

// Update mapToPayload function
export const mapToPayload = (data: ProductFormData, tcId: string, productId?: string | null) => {
  return {
    tcId,
    ...(productId ? { productId } : {}),
    tcProductInformation: [
      {
        tcProductOrderNo: Array.isArray(data.productInfo.tcProductOrderNo)
          ? data.productInfo.tcProductOrderNo
          : [data.productInfo.tcProductOrderNo || ""],
        tcProductArticleNo: data.productInfo.tcProductArticleNo || "",
        tcProductProductionDate: data.productInfo.tcProductProductionDate || "",
        tcProductShipmentDocNo: Array.isArray(data.productInfo.tcProductShipmentDocNo)
          ? data.productInfo.tcProductShipmentDocNo
          : [data.productInfo.tcProductShipmentDocNo || ""],
        tcProductNumberOfUnits: data.productInfo.tcProductNumberOfUnits || "0",
        tcProductUnitsMetric: data.productInfo.tcProductUnitsMetric || "",
        tcProductUnitsMetricOther: data.productInfo.tcProductUnitsMetricOther || "",
        tcProductNetShippingWeight: data.productInfo.tcProductNetShippingWeight || 0,
        tcProductSupplementaryWeight: data.productInfo.tcProductSupplementaryWeight || 0,
        tcProductInfoSupplementaryWeight: data.productInfo.tcProductInfoSupplementaryWeight || "",
        tcProductCategoryCode: data.productInfo.tcProductCategoryCode || "",
        tcProductCategoryUserSpecificTerm: data.productInfo.tcProductCategoryUserSpecificTerm || "",
        tcProductDetailCode: data.productInfo.tcProductDetailCode || "",
        tcProductDetailUserSpecificTerm: data.productInfo.tcProductDetailUserSpecificTerm || "",
        tcProductCategoryCodeDescription: data.productInfo.tcProductCategoryCodeDescription || "",
        tcProductDetailCodeDescription: data.productInfo.tcProductDetailCodeDescription || "",
        tcProductRawMaterialInformation: (data.rawMaterialInfo || []).map(item => ({
          tcProductRawMaterialCode: item.tcProductRawMaterialCode || "",
          tcProductRawMaterialPercentage: Number(item.tcProductRawMaterialPercentage) || 0,
          tcProductRawMaterialUserSpecificTerm: item.tcProductRawMaterialUserSpecificTerm || "",
          tcProductRawMaterialCodeDescription: item.tcProductRawMaterialCodeDescription || "",
          // Step 2 extras (persisted for reload; stripped from final JSON by the backend whitelist)
          claimsCategory: item.claimsCategory || "",
          certified: !!item.certified
        })),
        tcProductCertifiedRawMaterialGeographicInformation:
          groupCertifiedRawMaterialGeographicInformation(data.geographicInfo || []),
        tcProductCertifiedWeightInformation: (data.productCertifiedWeightInfo || []).map(item => ({
          tcProductClaimsCategory: item.tcProductClaimsCategory || "",
          tcProductClaimsCategoryCertifiedWeight:
            item.tcProductClaimsCategoryCertifiedWeight ?? item.tcProductCertifiedWeight ?? 0,
          tcProductCertifiedWeight:
            item.tcProductClaimsCategoryCertifiedWeight ?? item.tcProductCertifiedWeight ?? 0,
          tcProductRawMaterialCodeClaimsCategory: item.tcProductRawMaterialCodeClaimsCategory || "",
          tcProductRawMaterialCodeClaimsCategoryDescription: item.tcProductRawMaterialCodeClaimsCategoryDescription || ""
        })),
        tcProductFacilityInformation: mapProductFacilitiesToPayload(
          data.productFacilitiesInfo || []
        ),
        tcProductFirstProcessorInformation: (data.productFirstProcessorInfo || []).map(item => ({
          tcProductFirstProcessorTeId: item.tcProductFirstProcessorTeId || "",
          tcProductFirstProcessCode: toStringArray(item.tcProductFirstProcessCode)
        })),
        tcProductAdditives: data.productInfo.tcProductAdditives || "",
        tcProductAdditivesWeight: data.productInfo.tcProductAdditivesWeight || 0,
        tcProductInfoFiberLength: data.additionalInfo.tcProductInfoFiberLength || "",
        tcProductInfoYarnCount: data.additionalInfo.tcProductInfoYarnCount || "",
        tcProductInfoFabricWeightAndConstruct: data.additionalInfo.tcProductInfoFabricWeightAndConstruct || "",
        tcProductInfoFabricWeightOfFinishedProduct: data.additionalInfo.tcProductInfoFabricWeightOfFinishedProduct || "",
        tcProductInfoTechnicalSpecifications: data.additionalInfo.tcProductInfoTechnicalSpecifications || "",
        tcProductFeedstock: data.additionalInfo.tcProductFeedstock || "",
        tcProductPercentTextileWaste: data.additionalInfo.tcProductPercentTextileWaste || "",
        tcProductPackagingFor: normalizeStringArray(data.additionalInfo.tcProductPackagingFor),
        tcProductGtin: data.additionalInfo.tcProductGtin || "",
        tcProductReclaimedMaterialSource: data.additionalInfo.tcProductReclaimedMaterialSource || "",
        tcProductInfoAdditional: data.additionalInfo.tcProductInfoAdditional || "",
        tcProductOriginOrganizationTeId: Array.isArray(data.additionalInfo.tcProductOriginOrganizationTeId)
          ? data.additionalInfo.tcProductOriginOrganizationTeId
          : data.additionalInfo.tcProductOriginOrganizationTeId
          ? [String(data.additionalInfo.tcProductOriginOrganizationTeId)]
          : [],
        tcProductOriginFacilityTeId: Array.isArray(data.additionalInfo.tcProductOriginFacilityTeId)
          ? data.additionalInfo.tcProductOriginFacilityTeId
          : data.additionalInfo.tcProductOriginFacilityTeId
          ? [String(data.additionalInfo.tcProductOriginFacilityTeId)]
          : []
      }
    ]
  };
};

export const payloadToMap = (payload: any): ProductFormData => {
  Utills.cl(payload, "map");
  
  // Extract the first item from tcProductInformation array
  const productInfo = payload.tcProductInformation?.[0] || {};
  
  return {
    productInfo: {
      tcProductOrderNo: Array.isArray(productInfo.tcProductOrderNo) 
        ? productInfo.tcProductOrderNo 
        : [productInfo.tcProductOrderNo || ""],
      tcProductArticleNo: productInfo.tcProductArticleNo || "",
      tcProductProductionDate: productInfo.tcProductProductionDate || "",
      tcProductShipmentDocNo: Array.isArray(productInfo.tcProductShipmentDocNo) 
        ? productInfo.tcProductShipmentDocNo 
        : [productInfo.tcProductShipmentDocNo || ""],
      tcProductNumberOfUnits: productInfo.tcProductNumberOfUnits || "0",
      tcProductUnitsMetric: productInfo.tcProductUnitsMetric ? String(productInfo.tcProductUnitsMetric).toUpperCase() : "",
      tcProductUnitsMetricOther: productInfo.tcProductUnitsMetricOther || "",
      tcProductNetShippingWeight: productInfo.tcProductNetShippingWeight?.toString() || "0",
      tcProductSupplementaryWeight: productInfo.tcProductSupplementaryWeight?.toString() || "0",
      tcProductInfoSupplementaryWeight: productInfo.tcProductInfoSupplementaryWeight || "",
      tcProductAdditives: productInfo.tcProductAdditives || "",
      tcProductAdditivesWeight: productInfo.tcProductAdditivesWeight || 0,
      tcProductCategoryCode: productInfo.tcProductCategoryCode || "",
      tcProductCategoryUserSpecificTerm: productInfo.tcProductCategoryUserSpecificTerm || "",
      tcProductDetailCode: productInfo.tcProductDetailCode || "",
      tcProductDetailUserSpecificTerm: productInfo.tcProductDetailUserSpecificTerm || "",
      tcProductCategoryCodeDescription: productInfo.tcProductCategoryCodeDescription || "",
      tcProductDetailCodeDescription: productInfo.tcProductDetailCodeDescription || ""
    },
    rawMaterialInfo: (productInfo.tcProductRawMaterialInformation || []).map((item: any) => ({
      tcProductRawMaterialCode: item.tcProductRawMaterialCode || "",
      tcProductRawMaterialPercentage: item.tcProductRawMaterialPercentage?.toString() || "0",
      tcProductRawMaterialUserSpecificTerm: item.tcProductRawMaterialUserSpecificTerm || "",
      tcProductRawMaterialCodeDescription: item.tcProductRawMaterialCodeDescription || "",
      claimsCategory: item.claimsCategory || "",
      certified: !!item.certified
    })),
    geographicInfo: flattenCertifiedRawMaterialGeographicInformation(
      productInfo.tcProductCertifiedRawMaterialGeographicInformation ||
        productInfo.tcProductCertifiedRawMaterialGeographicInfo ||
        []
    ),
    productCertifiedWeightInfo: (productInfo.tcProductCertifiedWeightInformation || []).map((item: any) => ({
      tcProductClaimsCategory: item.tcProductClaimsCategory || "",
      tcProductCertifiedWeight:
        (item.tcProductClaimsCategoryCertifiedWeight ?? item.tcProductCertifiedWeight)?.toString() || "0",
      tcProductClaimsCategoryCertifiedWeight:
        item.tcProductClaimsCategoryCertifiedWeight ?? item.tcProductCertifiedWeight ?? 0,
      tcProductRawMaterialCodeClaimsCategory: item.tcProductRawMaterialCodeClaimsCategory || "",
      tcProductRawMaterialCodeClaimsCategoryDescription: item.tcProductRawMaterialCodeClaimsCategoryDescription || ""
    })),
    productFacilitiesInfo: normalizeProductFacilityInformation(
      productInfo.tcProductFacilityInformation ||
        productInfo.tcProductFacilitiesInformation ||
        []
    ),
    productFirstProcessorInfo: (productInfo.tcProductFirstProcessorInformation || []).map((item: any) => ({
      tcProductFirstProcessorTeId: item.tcProductFirstProcessorTeId || "",
      tcProductFirstProcessCode: toStringArray(item.tcProductFirstProcessCode)
    })),
    additionalInfo: {
      tcProductInfoFiberLength: productInfo.tcProductInfoFiberLength || "",
      tcProductInfoYarnCount: productInfo.tcProductInfoYarnCount || "",
      tcProductInfoFabricWeightAndConstruct: productInfo.tcProductInfoFabricWeightAndConstruct || "",
      tcProductInfoFabricWeightOfFinishedProduct: productInfo.tcProductInfoFabricWeightOfFinishedProduct || "",
      tcProductInfoAdditional: productInfo.tcProductInfoAdditional || "",
      tcProductAdditives: productInfo.tcProductAdditives || "",
      tcProductAdditivesWeight: productInfo.tcProductAdditivesWeight || 0,
      tcProductFeedstock: productInfo.tcProductFeedstock || "",
      tcProductPercentTextileWaste: productInfo.tcProductPercentTextileWaste || "",
      tcProductPackagingFor: normalizeStringArray(productInfo.tcProductPackagingFor),
      tcProductGtin: productInfo.tcProductGtin || "",
      tcProductReclaimedMaterialSource: productInfo.tcProductReclaimedMaterialSource || "",
      tcProductInfoTechnicalSpecifications: productInfo.tcProductInfoTechnicalSpecifications || "",
      tcProductOriginOrganizationTeId: Array.isArray(productInfo.tcProductOriginOrganizationTeId)
        ? productInfo.tcProductOriginOrganizationTeId
        : productInfo.tcProductOriginOrganizationTeId
        ? [productInfo.tcProductOriginOrganizationTeId]
        : [],
      tcProductOriginFacilityTeId: Array.isArray(productInfo.tcProductOriginFacilityTeId)
        ? productInfo.tcProductOriginFacilityTeId
        : productInfo.tcProductOriginFacilityTeId
        ? [productInfo.tcProductOriginFacilityTeId]
        : []
    }
  };
};

export function transformShipmentDetails(response: any): Option[] {
  if (!response || !Array.isArray(response.tcShipmentConsigneeDetails)) {
    throw new Error("Invalid response format. Expected 'tcShipmentConsigneeDetails' to be an array.");
  }
  return response.data.tcShipmentConsigneeDetails.map((detail: { tcShipmentNo?: string }) => {
    Utills.cl(detail);
    const shipmentNo = detail.tcShipmentNo ?? "";
    return { value: shipmentNo, label: shipmentNo };
  });
}
